/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { css, StyleSheet } from 'aphrodite';
import { ActionButton, AlertBar, Modal, Text } from '@blend/enterprise-components';

import { DocumentTitles } from 'blb-components/AuthenticatedApp/DashboardPage/DocumentsCard/constants';
import { ModalContent } from 'blb-components/ModalContent';
import { remCalc } from 'blb-styles/functions';

import {
  DocumentDownloadParams,
  downloadSingleDocument,
  downloadMultipleDocuments,
} from 'blb-components/AuthenticatedApp/DashboardPage/DocumentActions/DocumentActions';

const styles = StyleSheet.create({
  alert: {
    marginTop: remCalc(24),
  },
  footer: {
    display: 'flex',
    flexDirection: 'row-reverse',
    marginTop: remCalc(32),
  },
  message: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: remCalc(8),
  },
});

export namespace DownloadInkSignInterruptModal {
  export interface Props {
    documentDownloadParams: DocumentDownloadParams;
    handleClose: (hasAcknowledged: boolean) => void;
    isOpen: boolean;
  }
}

export const DownloadInkSignInterruptModal: React.FC<DownloadInkSignInterruptModal.Props> = ({
  documentDownloadParams,
  handleClose,
  isOpen,
}) => {
  const title = DocumentTitles.UNSIGNED_ESIGN_DOCUMENTS;

  const onModalClose = () => {
    // passing `false` as acknowledgement as "continue" button was not clicked
    handleClose(false);
  };

  const onContinueClick = () => {
    // handle passing of acknowledgement on the click of the button
    // so that "escape" closing the modal doesn't trigger "accept" of modal
    handleClose(true);
    if (documentDownloadParams.documents.length > 1) {
      downloadMultipleDocuments(documentDownloadParams);
    } else {
      downloadSingleDocument(documentDownloadParams);
    }
  };

  const renderMessage = (): JSX.Element => (
    <div className={css(styles.message)}>
      <Text colorName="NIGHT_90" fontSize="md" weight="bold">
        The borrower has not eSigned the sign ahead closing documents yet!
      </Text>
      <Text colorName="NIGHT_90" fontSize="md">
        Please work with the borrower to ensure that these documents are eSigned before or during the closing
        appointment.
      </Text>
    </div>
  );

  return (
    <Modal contentLabel={title} handleClose={onModalClose} isOpen={isOpen}>
      <ModalContent handleClose={undefined} headerText={title} subHeaderText={undefined}>
        <div className={css(styles.alert)}>
          <AlertBar icon="warning" message={renderMessage()} theme="warn" />
        </div>
        <div className={css(styles.footer)}>
          <ActionButton
            disabled={false}
            id="download-task-unsigned-modal-submit"
            isLoading={false}
            label="Acknowledge and download"
            onClick={onContinueClick}
            theme="primary"
          />
        </div>
      </ModalContent>
    </Modal>
  );
};
